import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit } from '@angular/core';
import { first, orderBy } from 'lodash-es';
import { NgControl } from '@angular/forms';
import { CdkConnectedOverlay } from '@angular/cdk/overlay';
import { of } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { FilterSelectBase } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-select-base';
import {
  FilterHierarchySelect,
  FilterHierarchySelectOption,
} from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-hirerarchy-select.model';
import { InputValidator } from '@nimbus-library/features/shared/components/material/input-validator';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-filter-hierarchy-select',
  templateUrl: './nimbus-filter-hierarchy-select.component.html',
  styleUrls: ['./nimbus-filter-hierarchy-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterHierarchySelectComponent extends FilterSelectBase<FilterHierarchySelect> implements OnInit {
  @Input() protected selectionMaxLength: number = 37;
  @Input()
  @HostBinding('class.input-view')
  public isInputView = false;

  @Input() public inputValidator: InputValidator;
  @Input() public ngControl: NgControl;
  @Input() public warningMessage: string;

  private readonly formFieldMarginBottom: number = 40;
  private optionsCount: number;
  private maxDepth: number;
  public overlayWidth: number;

  public ngOnInit(): void {
    super.ngOnInit();
    this.optionsCount = FilterHierarchySelectOption.getCount(this.filter.options);
    this.maxDepth = FilterHierarchySelectOption.getMaxDepth(this.filter.options);
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    this.overlayWidth = this.maxDepth > 10 ? 400 : 300;

    if (this.isInputView) {
      this.cdkConnectedPositions.map((x) => {
        if (x.overlayY === 'top') {
          x.offsetY = 0 - this.formFieldMarginBottom;
        } else if (x.overlayY === 'bottom') {
          x.offsetY = 0;
        }
      });
    }
  }

  public override onAttach(overlay: CdkConnectedOverlay): void {
    super.onAttach(overlay);
    this.remeasureAfterRender();
  }

  protected override updateOverlayMaxHeight(): void {
    const element = this.filterOverlayRef?.nativeElement;

    if (!element || !this.overlayRef) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const { top, bottom } = element.getBoundingClientRect();
      const opensUpwards = !!this.overlayRef.overlayElement.style.bottom;
      const availableHeight = opensUpwards ? bottom : document.documentElement.clientHeight - top;

      const maxHeight = Math.max(0, availableHeight - this.formFieldMarginBottom);
      element.style.maxHeight = `${maxHeight}px`;
    });
  }

  protected getSummary(): string {
    if (!this.filterValue?.length || this.filterValue.length === this.optionsCount) {
      return this.translateService.instant(TranslationKeys.FilterAll);
    }

    const selection = orderBy(
      FilterHierarchySelectOption.getSelection(new Set(this.filterValue), this.filter.options),
      (c) => c.name.toLowerCase(),
      'asc',
    );

    if (this.isInputView) {
      return this.getControlSummary(selection);
    }

    if (!this.filterValue?.length || !this.filter.options?.length) {
      return this.translateService.instant(TranslationKeys.FilterAll);
    }

    const firstValue = first(selection);
    return firstValue ? (this.filterValue.length === 1 ? firstValue.name : `${firstValue.name} +${this.filterValue.length - 1}`) : '';
  }

  protected onFilterValueChanged(): void {}

  private remeasureAfterRender(): void {
    of({})
      .pipe(untilDestroyed(this))
      .subscribe(() => this.updateOverlayMaxHeight());
  }

  private getControlSummary(selection: FilterHierarchySelectOption[]): string {
    if (!this.filterValue?.length || !this.filter.options?.length || !selection.length) {
      return null;
    }

    let selectionNamesString = selection.map(({ name }) => name).join(', ');
    const suffix =
      selectionNamesString.length <= this.selectionMaxLength ? '' : `...${selection.length > 1 ? `+${selection.length.toString()}` : ''}`;

    selectionNamesString = selectionNamesString.slice(0, this.selectionMaxLength - suffix.length);

    return `${selectionNamesString}${suffix}`;
  }
}
